/**
 * Collections over MCP — DELETION (PLAN §H tranche 5). The three most
 * dangerous tools in the feature, beside the writes rather than inside them.
 *
 * ## Every delete here is permanent, on every device
 *
 * The removal reaches storage the same way every other collections write does:
 * `setCollections`, then the app's own debounced whole-workspace persist,
 * then `workspace_db_save_snapshot`, whose `sync_family` TOMBSTONES every row
 * absent from the array it is handed (`sync/store.rs:284-310`) and queues a
 * `delete` op per row to the outbox. So a request deleted here is deleted on
 * the user's other devices too, and nothing in this app un-deletes a record.
 *
 * That is why these three are `destructive: true`: they need the Collections
 * WRITE grant **and** the destructive switch, and neither alone is enough. The
 * switch now unlocks NINE tools rather than six, and the settings copy that
 * says so is pinned against the Rust set from both sides.
 *
 * ## O13 — `cascade: true` is an argument the MODEL must supply
 *
 * It is not a request for the USER to confirm; the destructive switch is that,
 * and it is already on by the time a delete tool is even visible. It turns
 * "one wrong id deletes a forty-request collection, permanently, with no undo"
 * into "one wrong id returns a LISTING of what would be deleted". The refusal
 * carries the listing, so the second call is informed rather than blind — and
 * the listing IS the plan that would run, not a second description of it.
 *
 * **O34, as built:**
 *
 *   * `request_delete` — never needs cascade. One request is one node.
 *   * `folder_delete` — needs cascade if the folder holds ANY child, a nested
 *     folder or a request, at any depth.
 *   * `collection_delete` — needs cascade ALWAYS, even for an empty
 *     collection, and `cascade` is `required` in its own JSON schema. Deleting
 *     a collection is never one thing from the user's point of view; it is a
 *     name they chose and a place they filed work in.
 *
 * ## O14 — children before parents, and what that means HERE
 *
 * On boards it is a durability property, because a board delete is N separate
 * transactions that can stop half-way. Here there is ONE array write and it
 * cannot half-fail, so a naive ordering would be ceremony. What is real on this
 * surface is a cascade plan that removes a PARENT and leaves a CHILD behind:
 * the child becomes an orphan in the stored tree — a request whose `folder_id`
 * names a folder that is gone renders in no folder at all.
 *
 * So the order is enforced as an invariant over every INTERMEDIATE state of
 * the plan (`mcp/collectionDeletion.js`): after each removal, the working tree
 * must have gained no orphan. Children-first satisfies it by construction;
 * parents-first breaks it at the first folder; a plan that forgets a nested
 * subfolder's requests breaks it too. Reversing the plan turns
 * `mcpCollectionDeletes.test.js` red, which is the point.
 *
 * ## The no-loss guard is NOT switched off for deletes
 *
 * `assertNoCollectionLost` refuses any write that drops a row the change did
 * not declare. A delete declares exactly the ids its plan removes, and the
 * guard exempts those and only those — so a cascade that took one node more
 * than it told the user about is still refused, and a plan and an array that
 * disagree are still refused. Switching the guard off for the one operation
 * that removes things would have removed it from the only place it can catch a
 * real loss.
 *
 * ## Open editors are deliberately left alone
 *
 * The app's own `deleteRequest` closes the tab of a request it removes
 * (`useCollections.js:519-521`). This server does not: an external agent
 * closing an editor would discard whatever the user had typed into it, with no
 * warning and no undo, in a window they may not be looking at. The cost is a
 * phantom tab, and it IS only a phantom — `saveRequest`'s local update arm is a
 * `.map(...)` over an array the record is no longer in
 * (`useCollections.js:1223-1233`), so a Ctrl+S on a deleted request changes
 * nothing and cannot resurrect it. Read the header of `mcp/requestTabSync.js`.
 *
 * What IS cleared is the SELECTION, when it points at something that no longer
 * exists — the app's own delete does the same, and a detail pane rendering a
 * deleted record is a bug rather than a courtesy.
 */
import { mcpError } from '../errors';
import { collectionNotFound } from '../collectionRecords';
import {
  applyCollectionsChange,
  findCollection,
  findFolder,
  findRequest,
} from '../collectionsDb';
import {
  applyDeletionPlan,
  countsOf,
  planCollectionDelete,
  planFolderDelete,
} from '../collectionDeletion';
import {
  useCollectionsStore,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
} from '../../store/collectionsStore';

/** The listing an O13 refusal carries, and the summary a success carries. */
function listingOf(plan) {
  const { requests, folders } = countsOf(plan);
  return {
    requests,
    folders,
    // Names and ids, never a url, a header or a body: a refusal is not a read
    // tool and must not become one by accident.
    wouldDelete: plan.map((s) => ({ kind: s.kind, id: s.id, name: s.name })),
  };
}

/** `{collections, requests}` for `assertNoCollectionLost`'s exemption. */
function declaredRemovals(plan) {
  return {
    collections: plan.filter((s) => s.kind === 'collection').map((s) => s.id),
    requests: plan.filter((s) => s.kind === 'request').map((s) => s.id),
  };
}

/**
 * Clear any selection pointing at a node that has just gone. Runs AFTER the
 * write, never before — a refused delete must leave the UI exactly as it was.
 */
function clearSelectionFor(plan) {
  const gone = new Set(plan.map((s) => `${s.kind}:${s.id}`));
  const state = useCollectionsStore.getState();
  if (state.selectedRequest && gone.has(`request:${state.selectedRequest.id}`)) {
    setSelectedRequest(null);
  }
  if (state.selectedCollection && gone.has(`collection:${state.selectedCollection.id}`)) {
    setSelectedCollection(null);
  }
  if (state.selectedFolder && gone.has(`folder:${state.selectedFolder.id}`)) {
    setSelectedFolder(null);
  }
}

/** One request. No cascade, ever — **O34**: one node is one node. */
export async function requestDelete(args = {}) {
  const outcome = await applyCollectionsChange((collections) => {
    const found = findRequest(collections, args.request_id);
    if (!found) {
      return {
        error: mcpError(
          'not_found',
          'No request with that id in the open workspace. Nothing was deleted. Call collections_list with include_requests and use an id it returns.',
          { requestId: String(args.request_id ?? '') }
        ),
      };
    }

    const plan = [
      {
        kind: 'request',
        id: String(found.request.id),
        name: String(found.request.name || ''),
        method: String(found.request.method || 'GET').toUpperCase(),
      },
    ];
    const applied = applyDeletionPlan(found.collection, plan);
    if (applied.error) {
      return { error: mcpError('write_failed', 'That delete could not be applied, so nothing was deleted.') };
    }

    return {
      next: collections.map((c) =>
        String(c.id) === String(found.collection.id) ? applied.collection : c
      ),
      allowRemovals: declaredRemovals(plan),
      result: {
        deleted: 'request',
        collectionId: String(found.collection.id),
        id: String(found.request.id),
        name: String(found.request.name || ''),
        count: 1,
        complete: true,
        removed: plan,
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
  if (!outcome?.error && outcome?.removed) clearSelectionFor(outcome.removed);
  return outcome;
}

/** One folder and, with `cascade: true`, everything under it. **O34**. */
export async function folderDelete(args = {}) {
  const outcome = await applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const folder = findFolder(collection, args.folder_id);
    if (!folder) {
      return {
        error: mcpError(
          'not_found',
          'No folder with that id in this collection. Nothing was deleted. Call collection_read and use a folder id it returns.',
          { collectionId: String(collection.id), folderId: String(args.folder_id ?? '') }
        ),
      };
    }

    const plan = planFolderDelete(collection, folder.id);
    // Everything except the folder itself. `cascade` is about the CHILDREN.
    const children = plan.filter((s) => s.id !== String(folder.id));
    if (children.length && args.cascade !== true) {
      return {
        error: mcpError(
          'cascade_required',
          'That folder still holds things, and deleting it deletes them permanently on every device — there is no undo. Nothing was deleted. Call again with cascade: true, or move the requests out first with request_move.',
          {
            collectionId: String(collection.id),
            folderId: String(folder.id),
            name: String(folder.name || ''),
            ...listingOf(plan),
          }
        ),
      };
    }

    const applied = applyDeletionPlan(collection, plan);
    if (applied.error) {
      // The invariant broke, which means the PLAN was wrong. Refusing is the
      // only safe answer: a partially-cascaded tree is worse than no delete.
      if (process.env.NODE_ENV !== 'test') console.warn('[mcp] refused a cascade:', applied.error);
      return {
        error: mcpError(
          'write_failed',
          'That delete would have left folders or requests with no parent, so nothing was deleted.'
        ),
      };
    }

    return {
      next: collections.map((c) => (String(c.id) === String(collection.id) ? applied.collection : c)),
      allowRemovals: declaredRemovals(plan),
      result: {
        deleted: 'folder',
        collectionId: String(collection.id),
        id: String(folder.id),
        name: String(folder.name || ''),
        count: plan.length,
        ...countsOf(plan),
        complete: true,
        removed: plan,
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
  if (!outcome?.error && outcome?.removed) clearSelectionFor(outcome.removed);
  return outcome;
}

/**
 * A whole collection: every request, every folder, then the collection itself.
 *
 * `cascade: true` is REQUIRED ALWAYS, empty collection included, and it is
 * `required` in the tool's own JSON schema so a client is told before the call
 * rather than after. **O34**, and it matches the app: deleting a collection is
 * never one thing.
 */
export async function collectionDelete(args = {}) {
  const outcome = await applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const plan = planCollectionDelete(collection);
    if (args.cascade !== true) {
      return {
        error: mcpError(
          'cascade_required',
          'Deleting a collection deletes the collection, every folder in it and every request in them, permanently — there is no undo, and it disappears on the user\'s other devices too. Nothing was deleted. Call again with cascade: true if that is what you want.',
          {
            collectionId: String(collection.id),
            name: String(collection.name || ''),
            ...listingOf(plan),
          }
        ),
      };
    }

    const applied = applyDeletionPlan(collection, plan);
    if (applied.error) {
      if (process.env.NODE_ENV !== 'test') console.warn('[mcp] refused a cascade:', applied.error);
      return {
        error: mcpError(
          'write_failed',
          'That delete would have left folders or requests with no parent, so nothing was deleted.'
        ),
      };
    }
    if (applied.collection !== null) {
      return { error: mcpError('write_failed', 'That delete did not remove the collection, so nothing was written.') };
    }

    return {
      next: collections.filter((c) => String(c.id) !== String(collection.id)),
      allowRemovals: declaredRemovals(plan),
      result: {
        deleted: 'collection',
        id: String(collection.id),
        name: String(collection.name || ''),
        count: plan.length,
        ...countsOf(plan),
        complete: true,
        removed: plan,
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
  if (!outcome?.error && outcome?.removed) clearSelectionFor(outcome.removed);
  return outcome;
}
